/**
 * OIDC Client for Keycloak Integration
 *
 * Implements the OAuth 2.0 Authorization Code flow with PKCE for secure
 * authentication with Keycloak. Handles token exchange, refresh, and
 * user synchronization with the domain database.
 *
 * Security considerations:
 * - Access tokens are stored in memory only (never persisted)
 * - Refresh tokens should be stored in httpOnly secure cookies
 * - All tokens are validated before use
 * - PKCE prevents authorization code interception attacks
 *
 * @module @project-x/auth/oidc/client
 */

import type {
  OIDCConfig,
  TokenResponse,
  TokenClaims,
  OIDCUser,
  EnsureUserResponse,
  OIDCError,
  AuthState,
  LoginOptions,
} from "./types";
import { generatePKCE, generateState, AUTH_STATE_KEY, AUTH_STATE_MAX_AGE } from "./pkce";
import { createOIDCConfig, getUsersUrl } from "./config";

const shouldLogOIDCDebug = typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

/**
 * Error thrown when OIDC operations fail.
 */
export class OIDCClientError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly details?: OIDCError;

  constructor(message: string, code: string, status?: number, details?: OIDCError) {
    super(message);
    this.name = "OIDCClientError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Parses an HTTP response body safely and normalizes non-JSON failures.
 *
 * Many auth failures return HTML/plain text through proxies. This helper keeps
 * the original status while producing a stable object for diagnostics.
 */
async function parseResponseSafely(response: Response): Promise<TokenResponse | EnsureUserResponse | OIDCError> {
  const raw = await response.text();
  if (!raw.trim()) {
    return {
      error: "empty_response",
      error_description: "Received empty response from upstream"
    } as OIDCError;
  }

  try {
    return JSON.parse(raw) as TokenResponse | EnsureUserResponse | OIDCError;
  } catch {
    return {
      error: "invalid_json_response",
      error_description: "Received non-JSON response from upstream",
      message: raw.slice(0, 500),
    } as OIDCError;
  }
}

/**
 * Decodes a JWT token and extracts its claims.
 * Does NOT validate the token signature (that should be done server-side).
 *
 * @param token - JWT token string
 * @returns Decoded token claims
 * @throws OIDCClientError if token cannot be decoded
 */
export function decodeToken(token: string): TokenClaims {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(padded);
    return JSON.parse(decoded) as TokenClaims;
  } catch (error) {
    throw new OIDCClientError(
      `Failed to decode token: ${error instanceof Error ? error.message : "Unknown error"}`,
      "token_decode_error",
    );
  }
}

/**
 * Checks if a token has expired.
 *
 * @param claims - Token claims with expiration
 * @param bufferSeconds - Number of seconds before expiry to consider expired (default: 30)
 * @returns True if token is expired or will expire within buffer
 */
export function isTokenExpired(claims: TokenClaims, bufferSeconds: number = 30): boolean {
  const now = Math.floor(Date.now() / 1000);
  return claims.exp <= now + bufferSeconds;
}

/**
 * Checks if the user's authentication is fresh enough for sensitive operations.
 * Uses the auth_time claim to determine when the user last authenticated.
 *
 * @param claims - Token claims
 * @param maxAgeSeconds - Maximum age in seconds (default: 5 minutes)
 * @returns True if authentication is fresh enough
 */
export function isAuthFresh(claims: TokenClaims, maxAgeSeconds: number = 300): boolean {
  if (!claims.auth_time) {
    return false;
  }
  const now = Math.floor(Date.now() / 1000);
  return now - claims.auth_time <= maxAgeSeconds;
}

/**
 * Extracts normalized roles from token claims.
 * Prefers explicit Gruv claims, then realm roles, then client roles.
 *
 * @param claims - Decoded token claims
 * @returns Distinct role names
 */
export function extractRolesFromClaims(claims: TokenClaims): string[] {
  const roleSources: readonly (readonly string[] | undefined)[] = [
    claims.gruv_roles,
    claims.realm_access?.roles,
  ];

  const directRoles = roleSources.find((roles) => Array.isArray(roles) && roles.length > 0);
  if (directRoles && directRoles.length > 0) {
    return Array.from(new Set(directRoles));
  }

  const resourceAccess = claims.resource_access;
  if (!resourceAccess) {
    return [];
  }

  const aggregated = Object.values(resourceAccess).flatMap((entry) => entry.roles ?? []);
  return Array.from(new Set(aggregated));
}

/**
 * Stores the auth state in localStorage before redirecting to Keycloak.
 * 
 * Note: We use localStorage instead of sessionStorage because some flows
 * (like Keycloak's first-broker-login email verification) open in a new tab,
 * and sessionStorage is not shared across tabs.
 *
 * @param state - Auth state to store
 */
export function storeAuthState(state: AuthState): void {
  if (typeof localStorage !== "undefined") {
    const stateJson = JSON.stringify(state);
    localStorage.setItem(AUTH_STATE_KEY, stateJson);
    if (shouldLogOIDCDebug) {
      console.log("[OIDC] Stored auth state:", {
        key: AUTH_STATE_KEY,
        state: state.state.substring(0, 10) + "...",
        returnTo: state.returnTo,
        hasCodeVerifier: !!state.codeVerifier,
      });
    }
  } else {
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] localStorage not available - cannot store auth state!");
    }
  }
}

/**
 * Retrieves and removes the auth state from localStorage.
 * 
 * Note: We use localStorage instead of sessionStorage because some flows
 * (like Keycloak's first-broker-login email verification) open in a new tab,
 * and sessionStorage is not shared across tabs.
 *
 * @returns Auth state if found and valid, null otherwise
 */
export function retrieveAuthState(): AuthState | null {
  if (typeof localStorage === "undefined") {
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] localStorage not available!");
    }
    return null;
  }

  // Debug: list auth-related localStorage keys
  const authKeys = Object.keys(localStorage).filter(k => k.includes('auth') || k.includes('px_'));
  if (shouldLogOIDCDebug) {
    console.log("[OIDC] Retrieving auth state, auth-related localStorage keys:", authKeys);
  }

  const stored = localStorage.getItem(AUTH_STATE_KEY);
  if (shouldLogOIDCDebug) {
    console.log("[OIDC] Auth state key:", AUTH_STATE_KEY, "value exists:", !!stored);
  }

  if (!stored) {
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] No auth state found in localStorage!");
    }
    return null;
  }

  // Remove the state immediately to prevent replay attacks
  localStorage.removeItem(AUTH_STATE_KEY);

  try {
    const state = JSON.parse(stored) as AuthState;
    const age = Date.now() - state.createdAt;

    console.log("[OIDC] Retrieved auth state:", {
      state: state.state.substring(0, 10) + "...",
      returnTo: state.returnTo,
      ageMs: age,
      maxAgeMs: AUTH_STATE_MAX_AGE,
    });

    // Check if state has expired
    if (age > AUTH_STATE_MAX_AGE) {
      if (shouldLogOIDCDebug) {
        console.warn("[OIDC] Auth state expired - age:", age, "max:", AUTH_STATE_MAX_AGE);
      }
      return null;
    }

    return state;
  } catch (e) {
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] Failed to parse auth state:", e);
    }
    return null;
  }
}

/**
 * Cleans up stale auth state from localStorage if it exists and is expired.
 * This is called on app initialization to prevent accumulation of orphaned states
 * (e.g., when a user starts auth flow but never completes it).
 */
export function cleanupStaleAuthState(): void {
  if (typeof localStorage === "undefined") {
    return;
  }

  const stored = localStorage.getItem(AUTH_STATE_KEY);
  if (!stored) {
    return;
  }

  try {
    const state = JSON.parse(stored) as AuthState;
    const age = Date.now() - state.createdAt;

    if (age > AUTH_STATE_MAX_AGE) {
      console.log("[OIDC] Cleaning up stale auth state (age:", Math.round(age / 1000), "seconds)");
      localStorage.removeItem(AUTH_STATE_KEY);
    }
  } catch {
    // Invalid JSON, remove it
    console.log("[OIDC] Cleaning up invalid auth state");
    localStorage.removeItem(AUTH_STATE_KEY);
  }
}

/**
 * Builds the authorization URL for initiating the OIDC flow.
 *
 * @param config - OIDC configuration
 * @param pkce - PKCE parameters
 * @param state - Random state value
 * @param prompt - Optional prompt parameter ('login' to force re-authentication)
 * @returns Authorization URL
 */
export function buildAuthorizationUrl(
  config: OIDCConfig,
  pkce: { codeChallenge: string; codeChallengeMethod: string },
  state: string,
  prompt?: "login" | "none",
  idpHint?: string,
  uiLocales?: string,
  kcLocale?: string,
): string {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes?.join(" ") ?? "openid email profile",
    state,
    code_challenge: pkce.codeChallenge,
    code_challenge_method: pkce.codeChallengeMethod,
  });

  if (prompt) {
    params.set("prompt", prompt);
  }

  if (idpHint) {
    params.set("kc_idp_hint", idpHint);
  }

  if (uiLocales) {
    params.set("ui_locales", uiLocales);
  }

  if (kcLocale) {
    params.set("kc_locale", kcLocale);
  }

  return `${config.authorizationEndpoint}?${params.toString()}`;
}

/**
 * Initiates the OIDC login flow by redirecting to Keycloak.
 *
 * @param config - OIDC configuration (optional, defaults to env-based config)
 * @param returnTo - Path to return to after login (optional)
 * @param forceReauth - If true, forces re-authentication even if already logged in
 */
export async function initiateLogin(
  config?: OIDCConfig,
  returnTo?: string | LoginOptions,
  forceReauth: boolean = false,
): Promise<void> {
  const oidcConfig = config ?? createOIDCConfig();
  const pkce = await generatePKCE();
  const state = generateState();
  const loginOptions: LoginOptions =
    typeof returnTo === "string" || typeof returnTo === "undefined" ? { returnTo } : returnTo;
  const resolvedUILocales = loginOptions.uiLocales?.trim() || "es";
  const resolvedKCOverride = loginOptions.kcLocale?.trim() || "es";

  // Store state for callback validation
  const authState: AuthState = {
    state,
    codeVerifier: pkce.codeVerifier,
    returnTo: loginOptions.returnTo,
    createdAt: Date.now(),
  };
  storeAuthState(authState);

  // Build and redirect to authorization URL
  const authUrl = buildAuthorizationUrl(
    oidcConfig,
    pkce,
    state,
    forceReauth ? "login" : undefined,
    loginOptions.idpHint,
    resolvedUILocales,
    resolvedKCOverride,
  );

  console.log("[OIDC] 🔐 Initiating login flow to Keycloak", {
    timestamp: new Date().toISOString(),
    clientId: oidcConfig.clientId,
    redirectUri: oidcConfig.redirectUri,
    authorizationEndpoint: oidcConfig.authorizationEndpoint,
    returnTo: loginOptions.returnTo,
    forceReauth,
    idpHint: loginOptions.idpHint,
    authUrl: authUrl.substring(0, 150) + "...", // Log partial URL for security
  });

  window.location.href = authUrl;
}

/**
 * Forces re-authentication for sensitive operations.
 * Redirects to Keycloak with prompt=login to ensure fresh authentication.
 *
 * @param config - OIDC configuration (optional)
 * @param returnTo - Path to return to after re-authentication
 */
export async function forceReauth(config?: OIDCConfig, returnTo?: string): Promise<void> {
  return initiateLogin(config, returnTo, true);
}

/**
 * Exchanges an authorization code for tokens.
 *
 * @param config - OIDC configuration
 * @param code - Authorization code from callback
 * @param codeVerifier - PKCE code verifier
 * @returns Token response
 * @throws OIDCClientError if exchange fails
 */
export async function exchangeCode(
  config: OIDCConfig,
  code: string,
  codeVerifier: string,
): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
    code_verifier: codeVerifier,
  });

  // Use server-side proxy in the browser to avoid cross-origin SSL issues on mobile.
  // Mobile browsers may accept self-signed certs for navigation but reject them
  // for programmatic fetch() calls to different origins (e.g., auth.gruv.fm).
  const isBrowser = typeof window !== "undefined";
  const tokenEndpoint = isBrowser
    ? "/api/auth/token-exchange"
    : config.tokenEndpoint;

  console.log("[OIDC] 🔄 Exchanging authorization code for tokens", {
    timestamp: new Date().toISOString(),
    tokenEndpoint,
    proxied: isBrowser,
    clientId: config.clientId,
    redirectUri: config.redirectUri,
  });

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  const data = (await parseResponseSafely(response)) as TokenResponse | OIDCError;

  if (!response.ok) {
    const error = data as OIDCError;
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] ❌ Token exchange failed", {
        timestamp: new Date().toISOString(),
        status: response.status,
        statusText: response.statusText,
      });
    }
    throw new OIDCClientError(
      error.error_description ?? error.message ?? "Token exchange failed",
      error.error ?? "token_error",
      response.status,
      error,
    );
  }

  const tokens = data as TokenResponse;
  if (shouldLogOIDCDebug) {
    console.log("[OIDC] ✅ Token exchange successful", {
      timestamp: new Date().toISOString(),
      expiresIn: tokens.expires_in,
      tokenType: tokens.token_type,
      hasRefreshToken: !!tokens.refresh_token,
      hasIdToken: !!tokens.id_token,
    });
  }
  return tokens;
}

/**
 * Refreshes tokens using a refresh token.
 *
 * @param config - OIDC configuration
 * @param refreshToken - Refresh token
 * @returns New token response
 * @throws OIDCClientError if refresh fails
 */
export async function refreshTokens(config: OIDCConfig, refreshToken: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: config.clientId,
    refresh_token: refreshToken,
  });

  // Use server-side proxy in the browser (same reason as exchangeCode)
  const isBrowser = typeof window !== "undefined";
  const tokenEndpoint = isBrowser
    ? "/api/auth/token-exchange"
    : config.tokenEndpoint;

  if (shouldLogOIDCDebug) {
    console.log("[OIDC] 🔄 Refreshing tokens via Keycloak", {
      timestamp: new Date().toISOString(),
      tokenEndpoint,
      proxied: isBrowser,
      clientId: config.clientId,
      hasRefreshToken: !!refreshToken,
    });
  }

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  const data = (await parseResponseSafely(response)) as TokenResponse | OIDCError;

  if (!response.ok) {
    const error = data as OIDCError;
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] ❌ Token refresh failed", {
        timestamp: new Date().toISOString(),
        status: response.status,
        statusText: response.statusText,
      });
    }
    throw new OIDCClientError(
      error.error_description ?? error.message ?? "Token refresh failed",
      error.error ?? "refresh_error",
      response.status,
      error,
    );
  }

  const tokens = data as TokenResponse;
  if (shouldLogOIDCDebug) {
    console.log("[OIDC] ✅ Token refresh successful", {
      timestamp: new Date().toISOString(),
      expiresIn: tokens.expires_in,
      tokenType: tokens.token_type,
      hasRefreshToken: !!tokens.refresh_token,
    });
  }
  return tokens;
}

/**
 * Synchronizes the user with the domain database via /users/ensure.
 *
 * @param accessToken - Keycloak access token
 * @param usersUrl - Users service URL (optional, defaults to env-based URL)
 * @returns User information from domain DB
 * @throws OIDCClientError if sync fails
 */
export async function ensureUser(accessToken: string, usersUrl?: string): Promise<EnsureUserResponse> {
  const url = usersUrl ?? getUsersUrl();
  const endpoint = `${url}/ensure`;

  if (shouldLogOIDCDebug) {
    console.log("[OIDC] Ensuring user exists in domain DB");
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  const data = (await parseResponseSafely(response)) as EnsureUserResponse | OIDCError;

  if (!response.ok) {
    const error = data as OIDCError;
    if (shouldLogOIDCDebug) {
      console.error("[OIDC] User sync failed:", error);
    }
    throw new OIDCClientError(
      error.message ?? error.error_description ?? "User sync failed",
      error.error ?? "ensure_error",
      response.status,
      error,
    );
  }

  if (shouldLogOIDCDebug) {
    console.log("[OIDC] User synced successfully:", {
      userId: (data as EnsureUserResponse).user_id,
      created: (data as EnsureUserResponse).created,
    });
  }

  return data as EnsureUserResponse;
}

/**
 * Creates an OIDCUser from token claims and ensure response.
 *
 * @param claims - Decoded token claims
 * @param ensureResponse - Response from /users/ensure
 * @returns OIDCUser object
 */
export function createUser(claims: TokenClaims, ensureResponse: EnsureUserResponse): OIDCUser {
  const roles = extractRolesFromClaims(claims);
  const profileComplete = ensureResponse.profile_complete ?? ensureResponse.user.profile_complete;

  return {
    id: ensureResponse.user_id,
    encodedId: ensureResponse.user.encoded_id,
    sub: claims.sub,
    email: ensureResponse.user.email,
    username: ensureResponse.user.username,
    displayName: ensureResponse.user.display_name,
    status: ensureResponse.user.status,
    roles: roles,
    authTime: claims.auth_time,
    profileComplete,
    emailVerified: claims.email_verified ?? false,
  };
}

/**
 * Waits for a short delay between retry attempts.
 *
 * @param ms - Milliseconds to wait
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Builds the logout URL for ending the session.
 *
 * @param config - OIDC configuration
 * @param idToken - ID token for logout hint (optional)
 * @returns Logout URL
 */
export function buildLogoutUrl(config: OIDCConfig, idToken?: string): string {
  const params = new URLSearchParams();

  if (config.postLogoutRedirectUri) {
    params.set("post_logout_redirect_uri", config.postLogoutRedirectUri);
  }

  if (idToken) {
    params.set("id_token_hint", idToken);
  }

  params.set("client_id", config.clientId);

  return `${config.logoutEndpoint}?${params.toString()}`;
}

/**
 * Initiates logout by redirecting to Keycloak logout endpoint.
 *
 * @param config - OIDC configuration (optional)
 * @param idToken - ID token for logout hint (optional)
 */
export function initiateLogout(config?: OIDCConfig, idToken?: string): void {
  const oidcConfig = config ?? createOIDCConfig();
  const logoutUrl = buildLogoutUrl(oidcConfig, idToken);

  console.log("[OIDC] 🚪 Initiating logout, redirecting to Keycloak", {
    timestamp: new Date().toISOString(),
    logoutEndpoint: oidcConfig.logoutEndpoint,
    postLogoutRedirectUri: oidcConfig.postLogoutRedirectUri,
    clientId: oidcConfig.clientId,
    hasIdToken: !!idToken,
    logoutUrl: logoutUrl.substring(0, 150) + "...", // Log partial URL for security
  });
  window.location.href = logoutUrl;
}

/**
 * Handles the OIDC callback after user authenticates with Keycloak.
 * Validates the state, exchanges the code, syncs the user, and returns session data.
 *
 * @param config - OIDC configuration (optional)
 * @returns Object containing tokens, user, and return path
 * @throws OIDCClientError if callback handling fails
 */
export async function handleCallback(config?: OIDCConfig): Promise<{
  tokens: TokenResponse;
  claims: TokenClaims;
  user: OIDCUser;
  returnTo: string | undefined;
}> {
  const oidcConfig = config ?? createOIDCConfig();

  console.log("[OIDC] handleCallback started", {
    url: window.location.href,
    origin: window.location.origin,
    tokenEndpoint: oidcConfig.tokenEndpoint,
    clientId: oidcConfig.clientId,
  });

  // Parse URL parameters
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");
  const errorDescription = params.get("error_description");
  const iss = params.get("iss");

  console.log("[OIDC] 📋 Callback parameters received", {
    timestamp: new Date().toISOString(),
    hasCode: !!code,
    codeLength: code?.length || 0,
    state: state ? state.substring(0, 10) + "..." : null,
    hasError: !!error,
    error,
    errorDescription,
    issuer: iss,
  });

  // Handle error response from Keycloak
  if (error) {
    throw new OIDCClientError(errorDescription ?? error, error);
  }

  // Validate required parameters
  if (!code || !state) {
    throw new OIDCClientError("Missing code or state in callback", "invalid_callback");
  }

  // Retrieve and validate stored state
  const storedState = retrieveAuthState();
  if (!storedState) {
    // If session_state is present, Keycloak completed an IDP-link email verification
    // flow and resumed the original authorization code in a new browser context where
    // the PKCE state was not available. The account linking is already done in Keycloak;
    // the user just needs a fresh login to obtain tokens.
    const sessionState = params.get("session_state");
    if (sessionState) {
      throw new OIDCClientError(
        "IDP account linked — please log in again to complete sign-in",
        "idp_link_resume",
      );
    }
    throw new OIDCClientError("No stored auth state found (session expired?)", "state_not_found");
  }

  if (storedState.state !== state) {
    throw new OIDCClientError("State mismatch - possible CSRF attack", "state_mismatch");
  }

  // Exchange code for tokens
  const tokens = await exchangeCode(oidcConfig, code, storedState.codeVerifier);

  // Decode and validate access token
  const claims = decodeToken(tokens.access_token);
  if (isTokenExpired(claims, 0)) {
    throw new OIDCClientError("Received expired token", "token_expired");
  }

  // Sync user with domain DB
  console.log("[OIDC] 👤 Syncing user with domain DB", {
    timestamp: new Date().toISOString(),
    userId: claims.sub,
    email: claims.email,
  });
  const ensureResponse = await ensureUser(tokens.access_token);

  // Refresh tokens if gruv_user_id is missing after ensure
  let resolvedTokens = tokens;
  let resolvedClaims = claims;
  if (!resolvedClaims.gruv_user_id) {
    const maxRetries = 2;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log("[OIDC] 🔄 Token lacks gruv_user_id, refreshing...", {
        attempt,
        maxRetries,
      });
      if (!resolvedTokens.refresh_token) {
        break; // Can't refresh without refresh token
      }
      resolvedTokens = await refreshTokens(oidcConfig, resolvedTokens.refresh_token);
      resolvedClaims = decodeToken(resolvedTokens.access_token);
      if (resolvedClaims.gruv_user_id) {
        console.log("[OIDC] ✅ Token refreshed with gruv_user_id", {
          gruv_user_id: resolvedClaims.gruv_user_id,
        });
        break;
      }
      await delay(500);
    }
    if (!resolvedClaims.gruv_user_id) {
      if (shouldLogOIDCDebug) {
        console.warn("[OIDC] ⚠️ gruv_user_id still missing after refresh attempts");
      }
    }
  }

  // Create user object
  const user = createUser(resolvedClaims, ensureResponse);

  if (shouldLogOIDCDebug) {
    console.log("[OIDC] ✅ Callback handling successful", {
      timestamp: new Date().toISOString(),
      userId: user.id,
      email: user.email,
      roles: user.roles,
      returnTo: storedState.returnTo,
    });
  }

  return {
    tokens: resolvedTokens,
    claims: resolvedClaims,
    user,
    returnTo: storedState.returnTo,
  };
}

/**
 * Role checking utilities for the user.
 */
export const roleHelpers = {
  /**
   * Checks if the user has a specific role.
   */
  hasRole: (user: OIDCUser | null, role: string): boolean => {
    return user?.roles.includes(role) ?? false;
  },

  /**
   * Checks if the user has any of the specified roles.
   */
  hasAnyRole: (user: OIDCUser | null, roles: readonly string[]): boolean => {
    if (!user) return false;
    return roles.some((role) => user.roles.includes(role));
  },

  /**
   * Checks if the user has all of the specified roles.
   */
  hasAllRoles: (user: OIDCUser | null, roles: readonly string[]): boolean => {
    if (!user) return false;
    return roles.every((role) => user.roles.includes(role));
  },

  /**
   * Checks if the user is an admin.
   */
  isAdmin: (user: OIDCUser | null): boolean => {
    return roleHelpers.hasRole(user, "admin");
  },

  /**
   * Checks if the user is a manager (has the base "manager" role or any
   * "manager:*" scoped role, e.g. venue_owner, events_manager, staff_manager).
   * This must stay in sync with the manager app's middleware and login checks,
   * which use the same "manager" || startsWith("manager:") rule.
   */
  isManager: (user: OIDCUser | null): boolean => {
    if (!user) return false;
    return user.roles.some((role) => role === "manager" || role.startsWith("manager:"));
  },

  /**
   * Checks if the user is a venue owner.
   */
  isVenueOwner: (user: OIDCUser | null): boolean => {
    return roleHelpers.hasRole(user, "manager:venue_owner");
  },

  /**
   * Checks if the user is a promoter.
   */
  isPromoter: (user: OIDCUser | null): boolean => {
    return roleHelpers.hasRole(user, "manager:promoter");
  },

  /**
   * Checks if the user is staff.
   */
  isStaff: (user: OIDCUser | null): boolean => {
    return roleHelpers.hasRole(user, "manager:staff");
  },
};
